import React, { useMemo, useRef } from "react";
import { Pie } from "@visx/shape";
import { Group } from "@visx/group";
import { scaleOrdinal } from "@visx/scale";
import { useTooltip, TooltipWithBounds, defaultStyles } from "@visx/tooltip";
import { Box, Flex, Icon, Text } from "@chakra-ui/react";
import { animated, useSpring } from "react-spring";
import {
  backgroundGrey,
  color4,
  primaryAlt,
  textSecondary,
} from "../../../../../utils/colors";
import { TbGenderFemale, TbGenderMale, TbGenderNeutrois } from "react-icons/tb";
import { useTranslation } from "react-i18next";

// Define colors for gender categories
const genderColors = {
  male: color4,
  female: primaryAlt,
  other: backgroundGrey,
};

// Gender donut chart component
const GenderDonutChart = ({ width, height, data }) => {
  const { t } = useTranslation();
  const chartRef = useRef(null); // Add this line

  const halfWidth = width / 2;
  const halfHeight = height / 2;

  // console.log("data", data);

  // if all data 0
  const total = Object.values(data).reduce((acc, value) => acc + value, 0);

  // console.log("total", total);
  const fakeData = {
    other: 100,
  };

  // Tooltip setup
  const {
    showTooltip,
    hideTooltip,
    tooltipData,
    tooltipTop,
    tooltipLeft,
    tooltipOpen,
  } = useTooltip();

  // Create a color scale based on gender categories
  const colorScale = useMemo(
    () =>
      scaleOrdinal({
        domain: Object.keys(data),
        range: Object.values(genderColors),
      }),
    [data]
  );

  // Use react-spring for smooth animation
  const { scale } = useSpring({
    from: { scale: 0 },
    to: { scale: 1 },
    config: { duration: 500 },
  });

  // console.log("data", data);

  // If width or height is too small, don't render the chart
  if (width < 10 || height < 10) return null;

  return (
    <>
      <Flex align="center">
        <Box position="relative" width={width} height={height} ref={chartRef}>
          <svg width={width} height={height}>
            <Group top={halfHeight} left={halfWidth}>
              <Pie
                data={
                  total === 0 ? Object.entries(fakeData) : Object.entries(data)
                }
                pieValue={(d) => d[1]}
                outerRadius={halfWidth}
                innerRadius={halfWidth - 16} // Inner radius for the donut shape
                cornerRadius={3}
                padAngle={0.02}
              >
                {(pie) => (
                  <>
                    {pie.arcs.map((arc, index) => {
                      const [gender, count] = arc.data;
                      const path = pie.path(arc);
                      const color = colorScale(gender);

                      return (
                        <animated.path
                          key={`pie-arc-${index}`}
                          d={scale.to((s) => path ?? "")}
                          fill={color}
                          onMouseMove={(event) => {
                            const { current } = chartRef;
                            if (current) {
                              const bounding = current.getBoundingClientRect();
                              showTooltip({
                                tooltipData: { gender, count },
                                tooltipTop: event.clientY - bounding.y,
                                tooltipLeft: event.clientX - bounding.x,
                              });
                            }
                          }}
                          onMouseLeave={hideTooltip}
                        />
                      );
                    })}
                  </>
                )}
              </Pie>

              {/* Center text showing total patients */}
              <text
                textAnchor="middle"
                fill={textSecondary}
                fontSize={12}
                dy=".33em"
                fontWeight="500"
              >
                {/* {total} */}
              </text>
            </Group>
          </svg>

          {tooltipData && tooltipOpen && (
            <TooltipWithBounds
              left={tooltipLeft}
              top={tooltipTop}
              style={{
                ...defaultStyles,
                boxShadow: "0px 2px 5px rgba(27, 47, 55, 0.2)",
                padding: "0.5rem 1rem",
                borderRadius: "1rem",
                backgroundColor: "#2D3748",
                color: "white",
                fontSize: "0.8rem",
                fontWeight: "500",
                whiteSpace: "nowrap",
              }}
            >
              <Flex align="center">
                <Flex
                  w="6px"
                  h="6px"
                  borderRadius="50%"
                  bg={colorScale(tooltipData.gender)} // Use the same color scale for the tooltip
                  mr={1.5}
                />
                <Text fontSize="0.8rem" fontWeight="500" mr={1}>
                  {t(`common.${tooltipData.gender}Plural`)} -
                </Text>
                <Text fontWeight="600">{Math.round(tooltipData.count)}%</Text>
              </Flex>
            </TooltipWithBounds>
          )}
        </Box>
        <Flex p="0 0 0 1rem" direction="column" gap="0.25rem">
          <Flex align="center">
            <Icon as={TbGenderFemale} color={textSecondary} />
            <Text
              fontSize="0.8rem"
              color={textSecondary}
              fontWeight="500"
              mr="0.25rem"
            >
              {t("common.femalePlural")}:
            </Text>

            <Text fontSize="0.8rem" color={textSecondary} fontWeight="600">
              {(data?.female ?? 0).toFixed(0)}%
            </Text>
          </Flex>

          <Flex align="center">
            <Icon as={TbGenderMale} color={textSecondary} />
            <Text
              fontSize="0.8rem"
              color={textSecondary}
              fontWeight="500"
              mr="0.25rem"
            >
              {t("common.malePlural")}:
            </Text>

            <Text fontSize="0.8rem" color={textSecondary} fontWeight="600">
              {(data?.male ?? 0).toFixed(0)}%
            </Text>
          </Flex>

          {/* Hide "Other" if its value is 0% */}
          {(data?.other ?? 0) > 0.1 && (
            <Flex align="center">
              <Icon as={TbGenderNeutrois} color={textSecondary} />
              <Text
                fontSize="0.8rem"
                color={textSecondary}
                fontWeight="500"
                mr="0.25rem"
              >
                {t("common.otherPlural")}:
              </Text>
              <Text fontSize="0.8rem" color={textSecondary} fontWeight="600">
                {(data?.other ?? 0).toFixed(0)}%
              </Text>
            </Flex>
          )}
        </Flex>
      </Flex>
    </>
  );
};

export default GenderDonutChart;
